import { ConditionType } from "@builder/hooks/useConditions";
import { ConditionDataQuery, useGetConditionData } from "@builder/hooks/useGetConditionData";
import { CONDITION_STRUCTURE, SOURCE_OPTIONS } from "@builder/utils/constants";
import { getConditionOptions, sourceOptions } from "@builder/components/app/Popup/popups/Conditions/utils";
import TextInput from "../TextInput";
import SkeletonInput from "../SkeletonInput";
import { MultiSelect } from "../SelectInputNew/MultiSelect";
import { useEffect, useMemo, useState } from "react";
import SingleSelect from "../SelectInputNew/SingleSelect";
import isEqual from "lodash.isequal";
import { FINAL_USERS, ORG_USERS, UserTypes } from "@builder/types/Users.type";
import { useAppSelector } from "@builder/store/store";

const combineNames = (firstName?: string, lastName?: string) => {
    return `${firstName ? firstName + " " : ""}${lastName ? lastName : ""}`;
};

const LIMIT = 25;

interface ConditionSelectorProps {
    condition: ConditionType;
    index: number;
    groupIndex: number;
    conditionsLength: number;
    conditionSources: string[];
    handleSourceChange: (index: number, value: string) => void;
    handleSelect: (index: number, key: CONDITION_STRUCTURE, value: string) => void;
    handleInput: (index: number, key: CONDITION_STRUCTURE, value: string, source: string) => void;
    removeCondition: (index: number) => void;
    isEdit?: boolean;
    error: Record<string, Record<string, boolean>> | null;
    isTrigger?: boolean;
}

const ConditionSelector = ({
    condition,
    index,
    groupIndex,
    conditionsLength,
    conditionSources,
    handleSourceChange,
    handleSelect,
    handleInput,
    removeCondition,
    isEdit,
    error,
    isTrigger = false
}: ConditionSelectorProps) => {
    const [query, setQuery] = useState<ConditionDataQuery>({ limit: LIMIT, offset: 0, search: "" });
    const user = useAppSelector(state => state.storeData.user);
    const isOrg = useMemo(() => {
        if (!user) return null;
        if (FINAL_USERS.includes(user?.type as UserTypes) || ORG_USERS.includes(user?.type as UserTypes)) {
            return true;
        }
        return false;
    }, [user]);
    const { data, dataByIds, isLoading, hasMore } = useGetConditionData({
        conditionSource: condition.source as SOURCE_OPTIONS,
        query,
        additionalDataIds: condition.target ? condition.target.split(", ").filter(Boolean) : [],
        isOrg: isOrg ?? false
    });
    const [accumulatedData, setAccumulatedData] = useState<any[]>([]);

    useEffect(() => {
        if (!data) return;

        setAccumulatedData(prev => {
            if (query.offset === 0) {
                return isEqual(prev, data) ? prev : data;
            }

            const map = new Map(prev.map(item => [item._id, item]));
            data.forEach(item => map.set(item._id, item));
            const merged = Array.from(map.values());

            return isEqual(prev, merged) ? prev : merged;
        });
    }, [data, query.offset]);

    const isNum = condition.source.toLowerCase().includes("value") || condition.source.includes("#") || condition.source.includes("Time");
    const isMulti = !condition.condition.toLowerCase().includes("is");

    const conditionTargetValue = useMemo(() => {
        const allOptions = [...(accumulatedData || []), ...(dataByIds || [])];

        if (isMulti) {
            const values = condition.target.split(", ");
            return values.map(value => allOptions.find(item => item._id === value)).filter(Boolean);
        }

        return allOptions.find(item => item._id === condition.target);
    }, [isMulti, condition.target, accumulatedData, dataByIds]);

    const filteredSourceOptions = sourceOptions.filter(
        option => !conditionSources.includes(option) && (isTrigger || option !== SOURCE_OPTIONS.IDLE_TIME)
    );
    const sourceOptionsObj = useMemo(() => filteredSourceOptions.map(option => ({ label: option, value: option })), [filteredSourceOptions]);
    const conditionOptionsObj = useMemo(
        () => getConditionOptions(condition.source).map(option => ({ label: option, value: option })),
        [condition.source]
    );
    // const usedSources = conditions.map(condition => condition.source);

    // const filteredSourceOptions = sourceOptions.filter(option => !usedSources.includes(option) && (isTrigger || option !== SOURCE_OPTIONS.IDLE_TIME));

    // let currentTargetOptions: { [key: string]: string } = {};

    // if (!isNum) {
    //     switch (condition.source.split(" ")[0].toLowerCase()) {
    //         case "new":
    //             currentTargetOptions = {
    //                 "POS order": "POS",
    //                 "Online order": "Online"
    //             };
    //             break;
    //         default:
    //             currentTargetOptions = {};
    //             break;
    //     }
    // }

    // let item = "";

    // if (itemNamesRef.current[index]) {
    //     item = itemNamesRef.current[index][condition.target];
    // }

    const shouldShowDelete = groupIndex !== 0 || conditionsLength > 1 || index !== 0 || (condition.source && condition.condition && condition.target);

    return (
        <div className="condition-selector">
            {shouldShowDelete ? (
                <div className="delete-condition-wrapper">
                    <div className="pointer" onClick={() => removeCondition(index)}>
                        <img src={"./trash.svg"} />
                    </div>
                </div>
            ) : null}
            <div className="conditions-selectors">
                <SingleSelect
                    value={condition.source ? { label: condition.source, value: condition.source } : null}
                    onChange={source => handleSourceChange(index, source?.value || "")}
                    options={sourceOptionsObj}
                    placeholder="Select condition"
                    getOptionValue={option => option.value}
                    getOptionLabel={option => option.label}
                />
                <SingleSelect
                    value={conditionOptionsObj.find(option => option.value === condition.condition) || null}
                    onChange={condition => handleSelect(index, CONDITION_STRUCTURE.CONDITION, condition?.value || "")}
                    options={conditionOptionsObj}
                    placeholder="Select state"
                    getOptionValue={option => option.value}
                    getOptionLabel={option => option.label}
                />
                {isNum ? (
                    <TextInput
                        title="Value"
                        layout="column"
                        callback={value => handleInput(index, CONDITION_STRUCTURE.TARGET, value, condition.source)}
                        value={condition.target || ""}
                        placeholder="Enter amount"
                        error={error?.[condition._id]?.target || false}
                        className="actions-tab-input"
                        suffix={condition.source.includes("Time") ? "seconds" : ""}
                        noTranslate
                    />
                ) : isLoading && isEdit ? (
                    <SkeletonInput />
                ) : isMulti ? (
                    <MultiSelect
                        value={conditionTargetValue as any[]}
                        options={(accumulatedData as any) || []}
                        placeholder="Select value"
                        onChange={value => handleSelect(index, CONDITION_STRUCTURE.TARGET, value?.map((item: any) => item._id).join(", ") || "")}
                        getOptionValue={option => option._id}
                        onLoadMore={() => setQuery({ ...query, offset: query.offset + LIMIT })}
                        hasMore={hasMore}
                        getOptionLabel={(option: any) => option.name || option.receiptId || combineNames(option.firstName, option.lastName) || ""}
                        onSearch={value => setQuery({ search: value, offset: 0, limit: LIMIT })}
                    />
                ) : (
                    <SingleSelect
                        value={conditionTargetValue as any}
                        onChange={value => handleSelect(index, CONDITION_STRUCTURE.TARGET, value?._id || "")}
                        options={accumulatedData || []}
                        placeholder="Select value"
                        getOptionValue={option => option._id}
                        getOptionLabel={(option: any) =>
                            option.name || option.receiptId || option.email || combineNames(option.firstName, option.lastName) || ""
                        }
                        onLoadMore={() => setQuery({ ...query, offset: query.offset + LIMIT })}
                        hasMore={hasMore}
                        onSearch={value => setQuery({ search: value, offset: 0, limit: LIMIT })}
                    />
                )}
            </div>
        </div>
    );
};

export default ConditionSelector;
